import { FormEvent, useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { isSupabaseConfigured, supabase } from '../../lib/supabase';
import AdsPanel from './components/AdsPanel';
import DesignPanel from './components/DesignPanel';
import VisitorsPanel from './components/VisitorsPanel';
import NotificationsPanel from './components/NotificationsPanel';
import AccessPanel from './components/AccessPanel';

type AdminTab = 'ads' | 'design' | 'visitors' | 'notifications' | 'access';

const tabs: { id: AdminTab; label: string; icon: string }[] = [
  { id: 'ads', label: 'الإعلانات', icon: 'ri-advertisement-line' },
  { id: 'design', label: 'التصميم', icon: 'ri-layout-4-line' },
  { id: 'visitors', label: 'الزوار', icon: 'ri-group-line' },
  { id: 'notifications', label: 'الإشعارات', icon: 'ri-notification-3-line' },
  { id: 'access', label: 'الحسابات والدخول', icon: 'ri-user-key-line' },
];

export default function AdminPage() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState<AdminTab>('ads');

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => setSession(nextSession));
    return () => listener.subscription.unsubscribe();
  }, []);

  async function login(event: FormEvent) {
    event.preventDefault();
    if (!supabase) return;
    setMessage('');
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setMessage('البريد الإلكتروني أو كلمة المرور غير صحيحة.');
    setLoading(false);
  }

  if (!isSupabaseConfigured) {
    return <div className="min-h-screen bg-[#171717] text-white flex items-center justify-center p-6" dir="rtl"><div className="max-w-xl w-full rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl"><h1 className="text-2xl font-bold text-[#F5A623]">لوحة تحكم الموقع</h1><p className="mt-4 text-gray-300 leading-8">اللوحة جاهزة، وباقي ربط Supabase.</p><div className="mt-5 rounded-2xl bg-black/30 p-4 text-sm text-gray-300 leading-7">أضف VITE_SUPABASE_URL و VITE_SUPABASE_PUBLISHABLE_KEY إلى ملف .env ثم أعد تشغيل الموقع.</div></div></div>;
  }

  if (loading) return <div className="min-h-screen bg-[#171717] text-white grid place-items-center">جاري التحميل...</div>;

  if (!session) {
    return (
      <div className="min-h-screen bg-[#171717] text-white flex items-center justify-center p-5" dir="rtl">
        <form onSubmit={login} className="w-full max-w-md rounded-3xl border border-white/10 bg-white/5 p-7 shadow-2xl">
          <div className="text-center mb-7"><div className="w-16 h-16 mx-auto rounded-2xl bg-[#F5A623] grid place-items-center text-3xl text-black font-black">M</div><h1 className="text-2xl font-bold mt-4">دخول الإدارة</h1><p className="text-sm text-gray-400 mt-2">هذه الصفحة مخصصة لصاحب الموقع فقط</p></div>
          <label className="block text-sm mb-2">البريد الإلكتروني</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="admin-input mb-4" required />
          <label className="block text-sm mb-2">كلمة المرور</label><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="admin-input mb-5" required />
          {message && <p className="text-sm text-red-400 mb-4">{message}</p>}
          <button className="w-full rounded-xl bg-[#F5A623] text-black font-bold py-3 hover:brightness-110 transition">تسجيل الدخول</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#151515] text-white" dir="rtl">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#151515]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div><h1 className="font-bold text-xl">لوحة تحكم الموقع</h1><p className="text-xs text-gray-400 mt-1">{session.user.email}</p></div>
          <div className="flex gap-2"><a href="/" className="rounded-xl border border-white/10 px-4 py-2 text-sm">عرض الموقع</a><button onClick={() => supabase?.auth.signOut()} className="rounded-xl bg-red-500/15 text-red-300 px-4 py-2 text-sm">خروج</button></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 pb-3 overflow-x-auto">
          <nav className="flex gap-2 min-w-max">
            {tabs.map((tab) => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition ${activeTab === tab.id ? 'bg-[#F5A623] text-black' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}><i className={`${tab.icon} text-lg`} />{tab.label}</button>)}
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 sm:p-6">
        {activeTab === 'ads' && <AdsPanel session={session} />}
        {activeTab === 'design' && <DesignPanel />}
        {activeTab === 'visitors' && <VisitorsPanel />}
        {activeTab === 'notifications' && <NotificationsPanel />}
        {activeTab === 'access' && <AccessPanel />}
      </main>
    </div>
  );
}
